import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, inr, imgUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ArrowLeft, Star, MapPin, Plus, ShoppingBag, Gift } from "lucide-react";
import { motion } from "framer-motion";
import { PackageCard } from "@/pages/Packages";

export default function OccasionBuilder() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem, count } = useCart();

  const { data, isLoading } = useQuery({
    queryKey: ["occasion", slug],
    queryFn: async () => (await api.get(`/occasions/${slug}`)).data,
  });

  if (isLoading || !data) return <div className="p-8 text-muted-foreground">Loading occasion...</div>;

  const addService = (opt) => {
    addItem(
      { id: opt.id, name: opt.name, price: opt.price, unit: opt.unit, image: opt.image },
      { id: opt.vendor_id, name: opt.vendor_name }
    );
  };

  return (
    <div className="pb-28">
      <div className="relative h-52 md:h-64">
        <img src={data.image} alt={data.name} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/30" />
        <button
          data-testid="occasion-back"
          onClick={() => navigate("/occasions")}
          className="absolute top-4 left-4 h-10 w-10 grid place-items-center glass rounded-full"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="absolute bottom-4 left-4 right-4 md:left-8 text-white">
          <p className="overline text-secondary">Build your {data.name.toLowerCase()} event</p>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl">{data.name}</h1>
          <p className="text-white/80 text-sm mt-1">Pick each service from your favourite vendor & price.</p>
        </div>
      </div>

      <div className="px-4 md:px-8 py-6 space-y-8">
        {data.packages?.length > 0 && (
          <section data-testid="occasion-packages">
            <div className="flex items-center gap-2 mb-3">
              <Gift className="h-5 w-5 text-primary" />
              <h2 className="font-display font-bold text-xl md:text-2xl tracking-tight">Curated {data.name} packages</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">All-inclusive bundles for {data.name.toLowerCase()} — add one and you're done.</p>
            <div className="grid md:grid-cols-2 gap-5">
              {data.packages.map((p, i) => <PackageCard key={p.id} p={p} index={i} />)}
            </div>
          </section>
        )}
        {data.service_groups.length === 0 && (
          <p className="text-muted-foreground" data-testid="occasion-empty">No services listed for this occasion yet.</p>
        )}
        {data.service_groups.map((group) => (
          <section key={group.type} data-testid={`svc-group-${group.type}`}>
            <div className="flex items-end justify-between mb-3">
              <h2 className="font-display font-bold text-xl md:text-2xl tracking-tight">{group.type}</h2>
              <span className="text-xs font-semibold text-muted-foreground">
                {group.options.length} vendor{group.options.length > 1 ? "s" : ""}
              </span>
            </div>
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1">
              {group.options.map((opt, i) => (
                <motion.div
                  key={opt.id}
                  data-testid={`svc-option-${opt.id}`}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.04, 0.3) }}
                  className="shrink-0 w-64 bg-card border border-stone-200 rounded-2xl overflow-hidden"
                >
                  <div className="relative h-32">
                    <img src={imgUrl(opt.image)} alt={opt.name} className="h-full w-full object-cover" />
                    <span className="absolute top-2 right-2 bg-white/95 text-xs font-bold px-2 py-1 rounded-lg flex items-center gap-1">
                      <Star className="h-3 w-3 fill-secondary text-secondary" /> {opt.vendor_rating}
                    </span>
                  </div>
                  <div className="p-3">
                    <p className="font-semibold leading-tight text-sm">{opt.name}</p>
                    {opt.desc && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2" data-testid={`svc-desc-${opt.id}`}>{opt.desc}</p>}
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {opt.vendor_name} · {opt.vendor_city}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="font-bold">
                        {inr(opt.price)} <span className="text-[11px] font-normal text-muted-foreground">{opt.unit}</span>
                      </p>
                      <button
                        data-testid={`occasion-add-${opt.id}`}
                        onClick={() => addService(opt)}
                        className="h-9 w-9 grid place-items-center bg-primary text-white rounded-full hover:bg-primary/90 transition-colors"
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>
        ))}
      </div>

      {count > 0 && (
        <div className="fixed bottom-16 md:bottom-4 left-0 right-0 z-40 px-4 md:px-8">
          <button
            data-testid="occasion-view-cart"
            onClick={() => navigate("/cart")}
            className="max-w-6xl mx-auto w-full bg-primary text-white rounded-full py-3.5 px-6 flex items-center justify-between font-semibold shadow-xl hover:bg-primary/90 transition-colors"
          >
            <span className="flex items-center gap-2">
              <ShoppingBag className="h-5 w-5" /> {count} item{count > 1 ? "s" : ""} in your event
            </span>
            <span>View cart →</span>
          </button>
        </div>
      )}
    </div>
  );
}
